import { Permission } from "@/shared/auth/permissions";
import {
  BANK_STATUTORY_ALIASES,
  BANK_STATUTORY_EXACT_ALIASES,
  BANK_STATUTORY_FIELDS,
} from "@/shared/import/bank-statutory-import-fields";
import type { ImportGroup } from "@/shared/import/import-groups";

/**
 * The people import catalogue: every employee field a migrating HRMS export
 * can land on, in three permission tiers that mirror the shore's handlers.
 *
 * - No `requires`: parsed by `people/imports/csv-parser.ts`.
 * - `HR_IMPORT_RUN`: claimed by the hr-core handler (`hr-import-columns.ts`).
 *   `holidayCalendarName` and `attendanceDeviceId` are accepted there but
 *   deliberately not offered here.
 * - `SALARY_ASSIGNMENT_MANAGE_WORKSPACE`: claimed by the payroll module's
 *   `compensation` handler, which also needs the `payroll` plan feature.
 * - `EMPLOYEE_PROFILE_EDIT_SENSITIVE_WORKSPACE`: claimed by the hr-core
 *   `bank_statutory` handler (`bank-statutory-import-fields.ts`), which also
 *   needs the `hr_core` plan feature. The hr handler needs no plan feature.
 *
 * The shore fails the WHOLE file when it sees a claimed column the caller has
 * no permission for, so a gated field is not merely hidden, it is dropped
 * from the rewritten CSV (`resolveImportGroup`).
 *
 * Field ORDER is load-bearing: `suggestMapping`'s loose pass runs in this
 * order, so the original people fields stay first and new ones are appended.
 * The screen regroups by `section` at render time.
 *
 * Aliases stay multi-word on purpose. A bare "status" or "state" alias would
 * pull "Marital Status" onto hrStatus and fail every row.
 */

const HR = Permission.HR_IMPORT_RUN;
const PAY = Permission.SALARY_ASSIGNMENT_MANAGE_WORKSPACE;

export const PEOPLE_GROUP: ImportGroup = {
  key: "people",
  fields: [
    { name: "email", displayName: "Email", required: true, section: "basics" },
    { name: "first_name", displayName: "First name", required: false, section: "basics" },
    { name: "last_name", displayName: "Last name", required: false, section: "basics" },
    { name: "title", displayName: "Title", required: false, section: "job" },
    { name: "employment_type", displayName: "Employment type", required: false, section: "job" },
    { name: "work_mode", displayName: "Work mode", required: false, section: "job" },
    { name: "location", displayName: "Location", required: false, section: "job" },
    { name: "employee_id", displayName: "Employee ID", required: false, section: "basics" },
    { name: "department", displayName: "Department", required: false, section: "job" },
    { name: "manager_email", displayName: "Manager email", required: false, section: "job" },
    {
      name: "role",
      displayName: "Role (admin/manager/member)",
      required: false,
      section: "basics",
    },
    { name: "nickname", displayName: "Nickname", required: false, section: "basics" },
    {
      name: "hire_date",
      displayName: "Date of joining",
      required: false,
      section: "job",
      // Membership.hireDate feeds attendance and leave; EmployeeProfile's
      // dateOfJoining feeds payroll. One field on screen, two columns sent.
      alsoSendAs: [{ name: "dateOfJoining", requires: HR }],
    },
    // HR profile (hr-core handler)
    {
      name: "designationName",
      displayName: "Designation",
      required: false,
      section: "job",
      requires: HR,
    },
    { name: "gradeName", displayName: "Grade", required: false, section: "job", requires: HR },
    {
      name: "confirmationDate",
      displayName: "Confirmation date",
      required: false,
      section: "job",
      requires: HR,
    },
    {
      name: "noticeDays",
      displayName: "Notice period (days)",
      required: false,
      section: "job",
      requires: HR,
    },
    {
      name: "personalEmail",
      displayName: "Personal email",
      required: false,
      section: "personal",
      requires: HR,
    },
    {
      name: "bloodGroup",
      displayName: "Blood group",
      required: false,
      section: "personal",
      requires: HR,
    },
    {
      name: "emergencyContactName",
      displayName: "Emergency contact name",
      required: false,
      section: "personal",
      requires: HR,
    },
    {
      name: "emergencyContactPhone",
      displayName: "Emergency contact phone",
      required: false,
      section: "personal",
      requires: HR,
    },
    {
      name: "emergencyContactRelation",
      displayName: "Emergency contact relation",
      required: false,
      section: "personal",
      requires: HR,
    },
    {
      name: "healthInsuranceProvider",
      displayName: "Health insurance provider",
      required: false,
      section: "personal",
      requires: HR,
    },
    {
      name: "healthInsuranceNumber",
      displayName: "Health insurance number",
      required: false,
      section: "personal",
      requires: HR,
    },
    {
      name: "hrStatus",
      displayName: "Employee status (active/notice_period)",
      required: false,
      section: "exit",
      requires: HR,
    },
    {
      name: "resignationLetterDate",
      displayName: "Resignation date",
      required: false,
      section: "exit",
      requires: HR,
    },
    {
      name: "relievingDate",
      displayName: "Relieving date",
      required: false,
      section: "exit",
      requires: HR,
    },
    {
      name: "workLocationState",
      displayName: "State code (e.g. KA)",
      required: false,
      section: "workLocation",
      requires: HR,
    },
    {
      name: "workLocationCountry",
      displayName: "Country code (e.g. IN)",
      required: false,
      section: "workLocation",
      requires: HR,
    },
    {
      name: "workLocationFrom",
      displayName: "Work location from",
      required: false,
      section: "workLocation",
      requires: HR,
    },
    // Compensation (payroll module's compensation handler)
    {
      name: "annualCtc",
      displayName: "Annual CTC",
      required: false,
      section: "compensation",
      requires: PAY,
      requiresFeature: "payroll",
    },
    {
      name: "salaryStructureName",
      displayName: "Salary structure",
      required: false,
      section: "compensation",
      requires: PAY,
      requiresFeature: "payroll",
    },
    {
      name: "ctcEffectiveFrom",
      displayName: "CTC effective from",
      required: false,
      section: "compensation",
      requires: PAY,
      requiresFeature: "payroll",
    },
    ...BANK_STATUTORY_FIELDS,
  ],
  aliases: {
    email: [
      "email",
      "emailaddress",
      "mail",
      "contactemail",
      "workemail",
      "officialemail",
      "officeemail",
      "companyemail",
    ],
    first_name: ["firstname", "first", "givenname", "name"],
    last_name: ["lastname", "surname", "familyname", "last"],
    title: ["title", "jobtitle", "position", "roletitle"],
    employment_type: ["employmenttype", "employment", "jobtype", "contracttype"],
    work_mode: ["workmode", "workingfrom", "officelocation", "worklocation", "remoteoffice"],
    location: ["location", "office", "city", "country"],
    employee_id: ["employeeid", "hrisid", "staffid", "employeenumber", "employeecode", "empid"],
    department: ["department", "dept", "team", "division"],
    manager_email: ["manageremail", "manager", "reportsto", "supervisor"],
    role: ["role", "systemrole", "workspacerole", "accesslevel"],
    nickname: ["nickname", "preferredname"],
    hire_date: ["hiredate", "dateofjoining", "doj", "joiningdate", "joindate", "startdate"],
    designationName: ["designationname", "designation"],
    gradeName: ["gradename", "grade"],
    confirmationDate: ["confirmationdate", "dateofconfirmation"],
    noticeDays: ["noticedays", "noticeperiod", "noticeperioddays"],
    personalEmail: ["personalemail", "personalemailaddress"],
    bloodGroup: ["bloodgroup"],
    emergencyContactName: ["emergencycontactname", "emergencycontact"],
    emergencyContactPhone: ["emergencycontactphone", "emergencycontactnumber", "emergencyphone"],
    emergencyContactRelation: ["emergencycontactrelation", "emergencyrelation", "relation"],
    healthInsuranceProvider: ["healthinsuranceprovider", "insuranceprovider"],
    healthInsuranceNumber: ["healthinsurancenumber", "healthinsuranceno", "insurancenumber"],
    hrStatus: ["hrstatus", "employeestatus", "employmentstatus"],
    resignationLetterDate: ["resignationletterdate", "resignationdate"],
    relievingDate: ["relievingdate", "lastworkingday", "lastworkingdate", "exitdate"],
    workLocationState: [
      "worklocationstate",
      "statecode",
      "workstate",
      "workingstate",
      "stateofwork",
      "locationstate",
    ],
    workLocationCountry: [
      "worklocationcountry",
      "countrycode",
      "workcountry",
      "workingcountry",
      "countryofwork",
    ],
    workLocationFrom: ["worklocationfrom", "worklocationeffectivefrom"],
    annualCtc: ["annualctc", "annualcosttocompany"],
    salaryStructureName: ["salarystructurename", "salarystructure"],
    ctcEffectiveFrom: ["ctceffectivefrom", "salaryeffectivefrom"],
    ...BANK_STATUTORY_ALIASES,
  },
  // A bare "State" column is the work state in HRMS exports, but "state" as a
  // substring alias would claim headers like "Statement Date", so it matches
  // only exactly.
  exactAliases: { ...BANK_STATUTORY_EXACT_ALIASES, workLocationState: ["state"] },
  uploadHint:
    "Required column: email. Any other column we recognise (case-insensitive) maps automatically, pick from a dropdown otherwise. Dates must be written as YYYY-MM-DD, for example 2024-03-15.",
  hint: "We auto-matched what we could. Adjust each Pulsewise field to the right column from your file. Unmapped columns are ignored. Dates must be YYYY-MM-DD.",
  readyMessage: "Looks good. Click Review to check every row before anything is imported.",
  missingMessage: "Map the Email column before continuing, every row needs one.",
  confirmLabel: "Review",
  pendingLabel: "Checking...",
};
